import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetEntityMembersByEntityIdQuery } from "_shared/entity-members/_api/entity-member-api";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import React, { useMemo } from "react";
import { useSession } from "users/__session/session-context";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(({ theme }) => ({
  ".MuiInputBase-input": {
    paddingLeft: theme.spacing(1),
  },
  width: "100%",
})) as typeof TextField;
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

type TeamLeaderSelectorProps = {
  teamLeaderUserId?: Guid;
  popoverAnchor?: HTMLDivElement;
  onTeamLeaderUpdated?: (newTeamLeader?: Individual) => void;
  onPopoverClose?: () => void;
};

export default function TeamLeaderSelector(props: Readonly<TeamLeaderSelectorProps>) {
  const { teamLeaderUserId, popoverAnchor, onTeamLeaderUpdated, onPopoverClose } = props;

  // #region State
  const [searchTerm, setSearchTerm] = React.useState<string>("");

  // #region Hooks
  const session = useSession();

  // #region Queries
  const entityMemberQuery = useGetEntityMembersByEntityIdQuery(
    { legalEntityId: session.user!.legalEntityId.value },
    { skip: !session.user }
  );

  const userIds: Set<string> = (() => {
    if (!entityMemberQuery.data) return new Set<string>();
    const ids = entityMemberQuery.data.map((em) => em.userId);
    if (teamLeaderUserId) ids.push(teamLeaderUserId.value);
    if (session.user?.id) ids.push(session.user.id.value);
    return new Set(ids);
  })();

  const individualQuery = useGetIndividualsByUserIdsQuery(
    userIds.size ? { userIds } : skipToken
  );

  const userPool = useMemo(() => {
    if (!individualQuery.data) return [];
    const individuals = Individual.fromJSONArray(individualQuery.data) ?? [];
    return individuals;
  }, [individualQuery.data]);

  const selectedTeamLeader = useMemo(() => {
    return userPool?.find((c) => c.userId?.isEqualTo(teamLeaderUserId));
  }, [userPool, teamLeaderUserId]);

  const teamLeaderFilterResults = useMemo(() => {
    if (!searchTerm) return userPool ?? [];
    return userPool?.filter((r) => r.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ?? [];
  }, [userPool, searchTerm]);

  function search(value: string) {
    setSearchTerm(value);
  }

  function saveChanges(teamLeader: Individual) {
    onTeamLeaderUpdated?.(teamLeader);
    onPopoverClose?.();
  }

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={() => onPopoverClose?.()}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Search Users"
          slotProps={{
            input: {
              startAdornment: <SearchIcon />,
              endAdornment: (
                <>
                  {searchTerm.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {individualQuery.isLoading && <LoaderProgress size={24} />}
        <ListContainer dense>
          {teamLeaderFilterResults.length < 1 && (
            <ListItem>
              <ListItemText
                primary="None Found"
                slotProps={{ primary: { sx: { paddingTop: 2, textAlign: "center", typography: "body1" } } }}
              />
            </ListItem>
          )}
          {teamLeaderFilterResults.length > 0 &&
            teamLeaderFilterResults.map((individual: Individual) => {
              const isSelected = selectedTeamLeader?.userId?.isEqualTo(individual.userId);

              return (
                <ListItemButton
                  key={individual.userId?.value}
                  disableGutters
                  selected={isSelected}
                  onClick={() => saveChanges(individual)}
                >
                  <ListItemAvatar>
                    {isSelected ? (
                      <SelectedIcon>
                        <DoneIcon />
                      </SelectedIcon>
                    ) : (
                      <IndividualAvatar individual={individual} />
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={individual.fullName}
                    secondary={individual.email?.value}
                  />
                </ListItemButton>
              );
            })}
        </ListContainer>
      </PopoverContainer>
    </Popover>
  );
}
